import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { Trophy, Sparkles, MapPin, Award } from "lucide-react";
import api, { formatDate, inr } from "@/lib/api";

const RANGES = [
  { k: "week",  l: "This Week" },
  { k: "month", l: "This Month" },
  { k: "all",   l: "All Time" },
];

// Map rewards.py rank keys → public gemstone image slugs used across the app.
const RANK_ICONS = {
  executive:        "executive.png",
  sr_executive:     "senior-executive.png",
  silver:           "silver.png",
  gold:             "gold.png",
  pearl:            "pearl.png",
  emerald:          "emerald.png",
  sapphire:         "sapphire.png",
  ruby:             "ruby.png",
  platinum:         "platinum.png",
  diamond:          "diamond.png",
  royal:            "royal-diamond.png",
  crown_ambassador: "crown-ambassador.png",
  imperial_crown:   "imperial-crown.png",
  global_crown:     "global-crown.png",
};

/**
 * Public leaderboard — every IBA who ranked up in the selected window,
 * newest first, with their rank badge, name, city and reward credited.
 * Route: /team-achievers
 */
export default function TeamAchievers() {
  const [range, setRange] = useState("week");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/public/team-achievers?range=${range}&limit=48`)
      .then((r) => { if (alive) setItems(r.data.items || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range]);

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-gold inline-flex items-center gap-1.5 justify-center">
            <Sparkles size={12} /> Wall of Achievers
          </div>
          <h1 className="font-serif text-3xl md:text-5xl text-navy mt-2">Team Achievers</h1>
          <p className="text-sm md:text-base text-navy/60 mt-3 max-w-2xl mx-auto">
            Celebrate every IBA who crossed a new rank. Recognition builds momentum — cheer them on!
          </p>
        </div>

        {/* Range switcher */}
        <div className="flex justify-center gap-2 flex-wrap mb-8" data-testid="achievers-range-switch">
          {RANGES.map((r) => (
            <button
              key={r.k}
              type="button"
              onClick={() => setRange(r.k)}
              data-testid={`achievers-range-${r.k}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm transition-colors ${
                range === r.k ? "bg-navy text-gold border-navy" : "bg-white text-navy border-line hover:border-gold"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="py-16 text-center text-sm text-navy/40">Loading achievers…</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center border border-dashed border-line rounded-sm bg-white">
            <Trophy className="mx-auto text-navy/30" size={40} />
            <div className="mt-3 font-serif text-lg text-navy">No rank-ups {range === "week" ? "this week" : range === "month" ? "this month" : "yet"}</div>
            <p className="text-sm text-navy/50 mt-1">Be the first to make the wall — build your team!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="achievers-grid">
            {items.map((it, i) => {
              const icon = RANK_ICONS[it.rank_key] || "executive.png";
              return (
                <article
                  key={`${it.user_code}-${i}`}
                  data-testid={`achiever-${it.user_code}`}
                  className="group bg-white border border-line hover:border-gold/50 hover:shadow-lg transition-all rounded-sm overflow-hidden"
                >
                  <div className="relative px-5 py-4 flex items-center gap-4 bg-gradient-to-r from-royal/5 to-transparent">
                    <div className="w-14 h-14 shrink-0 rounded-full bg-cream border border-gold/30 grid place-items-center overflow-hidden">
                      {it.picture ? (
                        <img src={it.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Award size={22} className="text-gold-deep" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg text-navy truncate">{it.name}</div>
                      <div className="text-[11px] text-navy/50 font-mono">{it.user_code}</div>
                    </div>
                    <img
                      src={`/rank-icons/${icon}`}
                      alt={it.rank_label}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                      className="w-12 h-12 object-contain shrink-0"
                    />
                  </div>
                  <div className="px-5 py-3 border-t border-line/70 text-sm">
                    <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-gold-deep">
                      <Trophy size={11} /> {it.rank_label}
                    </div>
                    <div className="mt-1.5 flex items-center justify-between text-xs text-navy/60">
                      <span className="inline-flex items-center gap-1"><MapPin size={11} className="text-navy/40" /> {it.city}</span>
                      <span>{it.annual_pairs} pairs</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px]">
                      <span className="text-navy/40">{formatDate(it.achieved_at)}</span>
                      {it.reward > 0 && <span className="font-semibold text-olive">+{inr(it.reward)}</span>}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
